import React from 'react';
import PropTypes from 'prop-types';
import { compose, withProps, branch, withStateHandlers, withState, mapProps, lifecycle } from 'recompose';
import Select from 'react-select';
import 'react-select/dist/react-select.css';
import { List, Map } from 'immutable';
import { connect } from 'react-redux';

import Button from '../../../Button';
import setTimeplanUnit from '../setTimeplanUnit';
import { createNewTimeplanUnit } from '../../../../lib/creators';
import defaultWorkType from '../defaultWorkType';
import { getDate } from '../../../../lib/dates';
import withWorkTypesFromTimeplanUnits from '../withWorkTypesFromTimeplanUnits';
import Modal from '../../../../components/Modal';
import formControl from '../../formControl';
import { withCustomers } from '../../../../containers';
import { agreementById } from '../../../../entities/Agreement/selectors';
import { workTypesByIds } from '../../../../entities/WorkType/selectors';
import TimeplanUnit from './TimeplanUnit';
import * as styles from '../timeplan.css';
import requireProps from '../../../../lib/requireProps';
import { useTranslation, withTranslation } from 'react-i18next';

const CustomerSelect = compose(
  withTranslation(),
  formControl,
  withCustomers,
  mapProps(({ customers, ...props }) => ({
    ...props,
    options: customers.map(({ id, name }) => ({ value: id, label: name })).toJS(),
  })),
)(({ value, setValue, options, t }) => (
  <div style={{ width: '100%' }}>
    <Select
      placeholder={t("Kund")}
      name="customer"
      options={options}
      value={value}
      onChange={setValue}
      simpleValue
      clearable={false}
    />
  </div>
));

const AddTpuModal = compose(
  withTranslation(),
  withState('customerId', 'setCustomerId', ({ customersIds }) => customersIds.first()),
  withProps(({ customerId, mappedCustomerWorkTypes, resources }) => ({
    workTypes: mappedCustomerWorkTypes.get(customerId),
    resourceId: resources.size === 1 ? resources.getIn(['0', 'id']) : null,
  })),
  requireProps(['workTypes']),
  withStateHandlers(({ date, resourceId, workTypes }) => ({
    timeplanUnit: createNewTimeplanUnit(
      getDate(date),
      resourceId,
      defaultWorkType(workTypes).id,
      '00:00',
      '00:00',
    ),
  }), {
    onChangeTimeplanUnits: () => (transformFunc, timeplanUnit, values) => {
      return { timeplanUnit: timeplanUnit.merge(values) };
    },
  }),
  lifecycle({
    componentDidUpdate(prevProps) {
      if (this.props.customerId !== prevProps.customerId) {
        const workTypes = this.props.mappedCustomerWorkTypes.get(this.props.customerId);
        const values = { workType: defaultWorkType(workTypes).id };
        this.props.onChangeTimeplanUnits(null, this.props.timeplanUnit, values);
      }
    },
  }),
  withProps(({ addTpu, customerId, timeplanUnit }) => ({
    save: () => {
      addTpu(customerId, timeplanUnit);
    },
  })),
)(({
  show,
  setShow,
  save,
  setCustomerId,
  customerId,
  customersIds,
  workTypes,
  timeplanUnit,
  onChangeTimeplanUnits,
  t
}) => (
  <Modal
    title={t("Lägg till arbetspass")}
    setShow={setShow}
    show={show}
    size="small"
  >
    <CustomerSelect customersIds={customersIds} setValue={setCustomerId} value={customerId} label={t("Välj kund")} />
    <TimeplanUnit
      timeplanUnit={timeplanUnit}
      workTypes={workTypes}
      absentReasons={new List()}
      onChangeTimeplanUnits={onChangeTimeplanUnits}
      disabled={false}
      hasPayment={false}
      isDeletable={false}
    />
    <Button onClick={save} icon="plus-circle" stretch variant="primary" testID="savePeriod">
      {t("Spara arbetspass")}
    </Button>
  </Modal>
));

function AddTpu({
  addTpu,
  show,
  setShow,
  showModal,
  customersIds,
  mappedCustomerWorkTypes,
  resources,
  date,
}) {
  const { t } = useTranslation()
  if (!setShow) {
    return (
      <div className={styles.addTpu}>
        <Button
          onClick={addTpu}
          icon="plus-circle"
          testID="addPeriod"
          stretch
        >
          {t("Lägg till nytt arbetspass")}
        </Button>
      </div>
    );
  }

  return (
    <div className={styles.addTpu}>
      <AddTpuModal
        show={show}
        setShow={setShow}
        addTpu={addTpu}
        customersIds={customersIds}
        mappedCustomerWorkTypes={mappedCustomerWorkTypes}
        date={date}
        resources={resources}
      />
      <Button
        onClick={showModal}
        icon="plus-circle"
        testID="addPeriod"
        stretch
      >
        {t("Lägg till nytt arbetspass")}
      </Button>
    </div>
  );
}

AddTpu.defaultProps = {
  show: false,
  setShow: undefined,
  showModal: undefined,
  customersIds: undefined,
  mappedCustomerWorkTypes: undefined,
};

AddTpu.propTypes = {
  addTpu: PropTypes.func.isRequired,
  show: PropTypes.bool,
  setShow: PropTypes.func,
  showModal: PropTypes.func,
  customersIds: PropTypes.instanceOf(List),
  mappedCustomerWorkTypes: PropTypes.instanceOf(Map),
};

const addSingleCustomer = withProps(
  ({ onChangeTimeplanUnits, resources, workTypes, date, timeplanUnits }) => ({
    addTpu: () => {
      const resourceId = resources.size === 1 ? resources.getIn(['0', 'id']) : null;
      const bookingId = timeplanUnits.first().get('booking');
      const customerId = timeplanUnits.first().get('customer');
      const newTPU = createNewTimeplanUnit(
        getDate(date),
        resourceId,
        defaultWorkType(workTypes).id,
        '00:00',
        '00:00',
      );
      onChangeTimeplanUnits(
        setTimeplanUnit,
        newTPU
          .set('booking', bookingId)
          .set('customer', customerId),
        {},
      );
    },
  }),
);


const addMultipleCustomers = compose(
  connect((state, { bookings, customersIds }) => {
    if (!customersIds) {
      return {};
    }
    const mappedCustomerWorkTypes = customersIds.reduce((acc, customerId) => {
      // Return workTypes connected to this customer
      const customerBookings = bookings.filter(b => b.customer === customerId);
      const agreementId = customerBookings.first().get('customerAgreement');
      const agreement = agreementById(state, agreementId);
      const workTypes = workTypesByIds(state, agreement.get('workTypes'));
      return acc.set(customerId, workTypes);
    }, new Map());
    return {
      mappedCustomerWorkTypes,
    };
  }),
  withProps(
    ({
      onChangeTimeplanUnits,
      timeplanUnits,
      setShow,
    }) => ({
      addTpu: (customerId, timeplanUnit) => {
        setShow(false);
        const bookingId = timeplanUnits
          .filter(({ customer }) => customer === customerId)
          .first()
          .get('booking');
        onChangeTimeplanUnits(
          setTimeplanUnit,
          timeplanUnit
            .set('booking', bookingId)
            .set('customer', customerId),
          {},
        );
      },
    }),
  ),
);

export default compose(
  branch(({ allTimeplanUnits }) => allTimeplanUnits.size > 0,
    mapProps(({ allTimeplanUnits, ...props }) => ({
      ...props,
      timeplanUnits: allTimeplanUnits,
    })),
  ),
  withWorkTypesFromTimeplanUnits,
  branch(({ workTypes }) => workTypes.size > 0,
    addSingleCustomer,
    compose(
      withProps(({ timeplanUnits }) => ({
        customersIds: timeplanUnits.map(({ customer }) => customer).toSet().toList(),
      })),
      withStateHandlers({ show: false }, {
        setShow: () => show => ({ show }),
        showModal: () => () => ({ show: true }),
      }),
      addMultipleCustomers,
    ),
  ),
)(AddTpu);
